import dayjs from 'dayjs';
import { CSSProperties, forwardRef, Ref, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useAuthUser, useUserPermissions } from 'auth/hooks';
import { AccessPermissions } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { FormProvider } from 'core/states';
import { CorporateLocationHelper } from 'corporate/helpers';
import { ICreateCustomerQuoteFromQuoteForm as IDuplicateQuoteForm } from 'quotes/components/QuotePartAddToQuote';
import { DEFAULT_VALID_UNTIL_OFFSET } from 'quotes/constants';
import { QuoteEntityType } from 'quotes/enums';
import { useQuoteActions } from 'quotes/hooks';
import { ICustomerQuote } from 'quotes/types';

import {
  CustomerQuoteUpsertForm,
  TCustomerQuoteUpsertDisabledFields,
  TCustomerQuoteUpsertHiddenFields,
} from './CustomerQuoteUpsertForm';
import { useAlternateDefaultValues } from './hooks';
import { mapCreateCustomerQuoteForm, mapUpdateCustomerQuoteForm } from './mappers';
import { upsertCustomerQuoteSchema } from './schema';
import { ICustomerQuoteUpsertForm, IQuickQuoteContactPreload } from './types';

interface IProps {
  isOpen: boolean;
  onClose?: () => void;
  onSubmitSuccess?: (data: ICustomerQuote) => void;
  defaultValues?: Partial<ICustomerQuoteUpsertForm | IDuplicateQuoteForm>;
  disabledFields?: TCustomerQuoteUpsertDisabledFields;
  hiddenFields?: TCustomerQuoteUpsertHiddenFields;
  entityType?: QuoteEntityType;
  quote?: ICustomerQuote;
  withoutForm?: boolean;
  stickySxProps?: {
    header?: CSSProperties;
    notification?: CSSProperties;
  };
  preloadContact?: IQuickQuoteContactPreload;
  enableProspectQuickQuote?: boolean;
}

export const CustomerQuoteUpsert = forwardRef(function CustomerQuoteUpsert(
  {
    isOpen = false,
    onClose,
    onSubmitSuccess,
    defaultValues,
    disabledFields = {},
    hiddenFields = {},
    entityType = QuoteEntityType.QUOTE,
    quote,
    withoutForm,
    stickySxProps,
    preloadContact,
    enableProspectQuickQuote,
  }: IProps,
  ref: Ref<HTMLFormElement>
) {
  const { user } = useAuthUser();
  const { hasPermission } = useUserPermissions();
  const { upsertCustomerQuote } = useQuoteActions({ entityType });

  const omitDefaultSalesLocation = hasPermission(
    AccessPermissions.QUOTE_OMIT_DEFAULT_SALES_LOCATION
  );

  const isEdit = !!defaultValues?.id;

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({
    quote,
    user,
  });

  const alternateDefaultValues = useAlternateDefaultValues({
    entityType,
    isAlternateVersionEnabled,
  });

  const customDefaultValues = useMemo(() => {
    const validUntilDefaultValue =
      entityType === QuoteEntityType.QUOTE ? dayjs().add(DEFAULT_VALID_UNTIL_OFFSET, 'day') : null;

    return {
      jobName: '',
      location:
        !omitDefaultSalesLocation &&
        user &&
        user.location &&
        CorporateLocationHelper.isSales(user.location, isAlternateVersionEnabled)
          ? {
              id: user.location.id,
              name: user.location.name,
            }
          : null,
      validUntil: validUntilDefaultValue,
      setBillToAsDefaultForContact: false,
      setShipToAsDefaultForContact: false,
      ...alternateDefaultValues,
      ...defaultValues,
      // Resolved after the spread, which is load-bearing: MainInfo passes {...quote} as
      // defaultValues, so resolving earlier would leak a pre-feature quote's null (or the raw
      // string-scalar CONCAT object) into the field instead of the coerced quote→user fallback.
      shippingLocation: CorporateLocationHelper.getDefaultShipping(
        [quote?.shippingLocation, user?.shippingLocation],
        isAlternateVersionEnabled
      ),
    };
  }, [
    entityType,
    user,
    isAlternateVersionEnabled,
    alternateDefaultValues,
    defaultValues,
    omitDefaultSalesLocation,
    quote,
  ]);

  const handleOnSubmit = () => async (data: ICustomerQuoteUpsertForm) => {
    const { success, quote } = await upsertCustomerQuote(
      isEdit
        ? mapUpdateCustomerQuoteForm(data.id, data, entityType, isAlternateVersionEnabled)
        : mapCreateCustomerQuoteForm(data, entityType, isAlternateVersionEnabled)
    );

    if (!success) {
      return;
    }

    onSubmitSuccess && onSubmitSuccess(quote);

    onClose && setTimeout(() => onClose(), 1000);
  };

  const handleOnClose = (reset: UseFormReset<ICustomerQuoteUpsertForm>) => {
    reset();
    onClose();
  };

  return (
    <FormProvider
      schema={upsertCustomerQuoteSchema({
        entityType,
        companySettings: user?.companySettings,
        isEdit,
        isAlternateVersionEnabled,
      })}
      defaultValues={customDefaultValues}
      resetDefaultValues
    >
      <CustomerQuoteUpsertForm<ICustomerQuoteUpsertForm>
        ref={ref}
        isOpen={isOpen}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        onSubmit={handleOnSubmit}
        onClose={handleOnClose}
        user={user}
        defaultValues={customDefaultValues}
        disabledFields={disabledFields}
        hiddenFields={hiddenFields}
        entityType={entityType}
        isEdit={isEdit}
        withoutForm={withoutForm}
        stickySxProps={stickySxProps}
        preloadContact={preloadContact}
        enableProspectQuickQuote={enableProspectQuickQuote}
      />
    </FormProvider>
  );
});
